import { UserCommunication } from '@this+that/models';

import { UserCommunicationsStore } from './types';

const ARRAY_SLICE_KEYS = [
  'primary',
  'promotions',
  'social',
  'updates',
  'forums',
  'draft',
  'sent',
  'spam',
  'trash',
  'all',
] as const;

type ArraySliceKey = (typeof ARRAY_SLICE_KEYS)[number];
type PersistedState = Partial<UserCommunicationsStore> | undefined;

const preferNonEmpty = <T>(current: T[], persisted: T[] | undefined): T[] =>
  current.length > 0 ? current : (persisted ?? []);

const pickArraySlices = (
  persisted: PersistedState,
  current: UserCommunicationsStore,
): Pick<UserCommunicationsStore, ArraySliceKey> => {
  const result = {} as Pick<UserCommunicationsStore, ArraySliceKey>;

  ARRAY_SLICE_KEYS.forEach((key) => {
    result[key] = preferNonEmpty(
      current[key] as UserCommunication[],
      persisted?.[key] as UserCommunication[] | undefined,
    );
  });

  return result;
};

const mergeLabels = (
  persisted: PersistedState,
  current: UserCommunicationsStore,
): Record<string, UserCommunication[]> => {
  const persistedLabels = persisted?.labels ?? {};
  const currentLabels = current.labels ?? {};
  const labelKeys = new Set([
    ...Object.keys(persistedLabels),
    ...Object.keys(currentLabels),
  ]);

  return Object.fromEntries(
    Array.from(labelKeys).map((key) => [
      key,
      preferNonEmpty(currentLabels[key] ?? [], persistedLabels[key]),
    ]),
  );
};

const mergeUnconfirmedDrafts = (
  persisted: PersistedState,
  current: UserCommunicationsStore,
): Record<string, number> => {
  const merged: Record<string, number> = {
    ...(persisted?.unconfirmedDrafts ?? {}),
  };

  Object.entries(current.unconfirmedDrafts ?? {}).forEach(
    ([draftId, savedAt]) => {
      merged[draftId] = Math.max(merged[draftId] ?? 0, savedAt);
    },
  );

  return merged;
};

const mergeDeletedDrafts = (
  persisted: PersistedState,
  current: UserCommunicationsStore,
): Record<string, number> => {
  const merged: Record<string, number> = {
    ...(persisted?.deletedDrafts ?? {}),
  };

  Object.entries(current.deletedDrafts ?? {}).forEach(
    ([draftId, deletedAt]) => {
      merged[draftId] = Math.max(merged[draftId] ?? 0, deletedAt);
    },
  );

  return merged;
};

type StartKeys = UserCommunicationsStore['startKeys'];
type StringStartKeyField = Exclude<keyof StartKeys, 'labels'>;

const STRING_START_KEY_FIELDS: StringStartKeyField[] = [
  'primary',
  'promotions',
  'social',
  'updates',
  'forums',
  'draft',
  'sent',
  'spam',
  'trash',
  'all',
];

const mergeStartKeys = (
  persisted: PersistedState,
  current: UserCommunicationsStore,
): StartKeys => {
  const persistedStartKeys = persisted?.startKeys;
  if (!persistedStartKeys) {
    return current.startKeys;
  }

  const merged: StartKeys = {
    ...current.startKeys,
    labels: { ...persistedStartKeys.labels, ...current.startKeys.labels },
  };

  STRING_START_KEY_FIELDS.forEach((field) => {
    if (current.startKeys[field] === null) {
      merged[field] = persistedStartKeys[field];
    }
  });

  return merged;
};

export const mergeHydratedUserCommunicationsState = (
  persisted: unknown,
  current: UserCommunicationsStore,
): UserCommunicationsStore => {
  const persistedState = persisted as PersistedState;

  return {
    ...current,
    ...pickArraySlices(persistedState, current),
    labels: mergeLabels(persistedState, current),
    unconfirmedDrafts: mergeUnconfirmedDrafts(persistedState, current),
    deletedDrafts: mergeDeletedDrafts(persistedState, current),
    startKeys: mergeStartKeys(persistedState, current),
  };
};
